import axiosInstance from '@/lib/axios';

export interface InvoiceItem {
  id?: string;
  description: string;
  longDescription?: string;
  quantity: number;
  unit: string;
  rate: number;
  tax: string;
  amount: number;
}

export interface CreateInvoiceRequest {
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  preventOverdueReminders?: boolean;
  tags?: string[];
  paymentModes?: string[];
  currency: string;
  saleAgent?: string;
  isRecurring?: boolean;
  discountType?: string;
  adminNote?: string;
  status?: 'paid' | 'pending' | 'overdue';
  items?: InvoiceItem[];
  quantityDisplay?: 'qty' | 'hours' | 'qty_hours';
  billToAddress?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  shipToAddress?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
}

export interface Invoice {
  id: string;
  customerId: string;
  customer?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address?: {
      address1: string;
      address2?: string;
      city: string;
      state: string;
      country: string;
      postalCode: string;
    };
  };
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
  preventOverdueReminders: boolean;
  tags: string[];
  paymentModes: string[];
  currency: string;
  saleAgent: string;
  isRecurring: boolean;
  discountType: string;
  adminNote?: string;
  items: InvoiceItem[];
  quantityDisplay?: 'qty' | 'hours' | 'qty_hours';
  billToAddress?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  shipToAddress?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
  subtotal: number;
  totalTax: number;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceFilters {
  search?: string;
  status?: string;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  forRevenue?: boolean;
}

export interface InvoicesResponse {
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface CreateInvoiceResponse {
  success: boolean;
  message: string;
  data: Invoice;
}

export interface InvoiceListResponse {
  success: boolean;
  message: string;
  data: Invoice[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

class InvoiceService {

  async getInvoices(filters: InvoiceFilters = {}): Promise<InvoicesResponse> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && key !== 'forRevenue') {
          params.append(key, value.toString());
        }
      });

      if (filters.forRevenue) {
        params.append('forRevenue', 'true');
      }

      const url = `/invoices?${params.toString()}`;
      console.log('🔄 Fetching invoices with filters:', filters);
      console.log('🌐 API URL:', url);
      
      const response = await axiosInstance.get(url);
      console.log('📊 Invoice list response:', response.data);
      
      // Handle different response formats
      if (response.data.success && response.data.data) {
        // Backend format: { success: true, data: [...], pagination: {...} }
        return {
          data: response.data.data,
          total: response.data.pagination?.total || response.data.data.length,
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 20,
          hasMore: response.data.pagination?.hasNextPage || false,
        };
      } else if (Array.isArray(response.data)) {
        // Direct array format
        return {
          data: response.data,
          total: response.data.length,
          page: 1,
          limit: response.data.length,
          hasMore: false,
        };
      } else {
        // Fallback format
        return {
          data: response.data.data || [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          limit: response.data.limit || 20,
          hasMore: response.data.hasMore || false,
        };
      }
    } catch (error: any) {
      console.error('❌ Error fetching invoices:', error);
      throw error;
    }
  }

  async getInvoice(id: string): Promise<Invoice> {
    try {
      console.log('🔄 Fetching invoice:', id);
      const response = await axiosInstance.get(`/invoices/${id}`);
      console.log('📊 Invoice details response:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching invoice:', error);
      throw error;
    }
  }

  async createInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    try {
      console.log('🔄 Creating invoice with data:', data);
      const response = await axiosInstance.post('/invoices', data);
      console.log('📊 Create invoice response:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      console.error('❌ Error creating invoice:', error);
      throw error;
    }
  }

  async updateInvoice(id: string, data: Partial<CreateInvoiceRequest>): Promise<Invoice> {
    try {
      console.log('🔄 Updating invoice:', id, 'with data:', data);
      const response = await axiosInstance.patch(`/invoices/${id}`, data);
      console.log('📊 Update invoice response:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    } catch (error: any) {
      console.error('❌ Error updating invoice:', error);
      throw error;
    }
  }

  async deleteInvoice(id: string): Promise<void> {
    try {
      console.log('🔄 Deleting invoice:', id);
      const response = await axiosInstance.delete(`/invoices/${id}`);
      console.log('📊 Delete invoice response:', response.data);
      
      if (response.data.success) {
        return response.data;
      }
    } catch (error: any) {
      console.error('❌ Error deleting invoice:', error);
      throw error;
    }
  }

  async sendInvoice(id: string): Promise<void> {
    await axiosInstance.post(`/invoices/${id}/send`);
  }

  async markAsPaid(id: string, paymentData: {
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    reference?: string;
  }): Promise<void> {
    await axiosInstance.post(`/invoices/${id}/payment`, paymentData);
  }

  async generateInvoiceNumber(): Promise<{ number: string }> {
    const response = await axiosInstance.get('/invoices/generate-number');
    return response.data;
  }


  async getPaymentModes(): Promise<Array<{ id: string; name: string; description?: string }>> {
    const response = await axiosInstance.get('/payment-modes');
    return response.data;
  }

  async getTaxRates(): Promise<Array<{ id: string; name: string; rate: number }>> {
    const response = await axiosInstance.get('/tax-rates');
    return response.data;
  }

  async getSaleAgents(): Promise<Array<{ id: string; name: string; email: string }>> {
    const response = await axiosInstance.get('/sale-agents');
    return response.data;
  }

  async getDiscountTypes(): Promise<Array<{ id: string; name: string; type: string }>> {
    const response = await axiosInstance.get('/discount-types');
    return response.data;
  }
}

export const invoiceService = new InvoiceService();
